import { CONVERSATION_QUERY_LIMIT } from 'constants/limits';
import { processAssignedTicket } from '../../util/dataObjectToStoreObject';
import { fetchConversationMessages } from '../messages';
import environment from 'environment';
import makeRequest from '../makeRequest';
import { processMessage } from 'util/dataObjectToStoreObject';
import * as sourceTypes from 'constants/sources';
import { getExpertId } from 'util/parseSelectors';
import { handleError } from 'services/ErrorService/ErrorService';
import makeCloudFunctionRequest from '../makeCloudFunctionRequest';
import {
  getAllTickets,
  getReviewTickets,
  getAssignedTicketsHelper,
  getTagHistoryAndCombinedTagsForAConversation,
  getConversationById,
  getEscalatedTickets,
} from './queries';

const SUBMIT_VALIDATION_URL = `${environment.app.serverApiUrl}/cloud-functions/v2/submitValidation`;
const VALIDATION_CONTENT_BASE_URL = `${environment.app.serverApiUrl}/cloud-functions/v2/validation-details/`;

export const fetchValidationTicketDetails = async (ticketId) => {
  const response = await makeRequest(VALIDATION_CONTENT_BASE_URL + ticketId);

  return response;
};

/**
 * @param {{ conversationId: string, simplrExpertId: string, expertResponse: string }} payload
 */
export const submitValidationResponse = async (payload) => {
  const response = await makeRequest(SUBMIT_VALIDATION_URL, { method: 'post', data: payload });

  return response;
};

export const getAssignedTickets = async (isAdmin = false, partnersAllowlist = []) => {
  // First get tickets with new messages (unreadcount > 0)
  let getUnreadTickets = true;
  // Get a maximum of 100 (or whatever the conversation query limit is) unread tickets
  const unReadTickets = await getAssignedTicketsHelper(
    isAdmin,
    getUnreadTickets,
    CONVERSATION_QUERY_LIMIT,
    partnersAllowlist
  );
  const numUnreadTickets = unReadTickets.length;

  // Then get tickets which have no new messages (unreadcount = 0)
  getUnreadTickets = false;
  const readTickets = await getAssignedTicketsHelper(
    isAdmin,
    getUnreadTickets,
    CONVERSATION_QUERY_LIMIT - numUnreadTickets,
    partnersAllowlist
  );

  const tickets = [...unReadTickets, ...readTickets];

  let assignedTickets = { allTickets: [] };

  for (let ticket of tickets) {
    let processedTicket = processAssignedTicket(ticket);

    if (!assignedTickets.allTickets.includes(processedTicket.ticketId)) {
      assignedTickets.allTickets.push(processedTicket.ticketId);
    } else {
      handleError({ customMessage: 'Duplicate ticket detected in assigned ticket queue' });
    }

    assignedTickets = Object.assign({}, assignedTickets, {
      [processedTicket.ticketId]: processedTicket,
      allTickets: Object.assign([], assignedTickets.allTickets),
    });
  }

  return assignedTickets;
};

export const sendTestTicketsToExpert = async (simplrExpertObjId, numTestTickets) => {
  const requestParams = { simplrExpertObjId };
  // eslint-disable-next-line eqeqeq
  if (numTestTickets != undefined) {
    requestParams.numOfTickets = numTestTickets;
  }

  return makeCloudFunctionRequest('sendTestTicketsToExpert', requestParams);
};

export const getConversation = async (conversationObjectId, isFlowDebugStateEnabled, simplrAccountId) => {
  const messages = await fetchConversationMessages(conversationObjectId, isFlowDebugStateEnabled, simplrAccountId);

  let result = { allMessages: [], selectedConversationId: '' };

  for (let message of messages) {
    if (message !== undefined) {
      let processedMessage = processMessage(message);
      result[processedMessage.messageId] = processedMessage;
      result.allMessages.push(processedMessage.messageId);
    }
  }
  result.selectedConversationId = conversationObjectId;

  return result;
};

export const updateParseConversation = (conversationId, updatedFields) => {
  const params = {
    conversationId: conversationId,
    updatedFields: updatedFields,
  };

  return makeCloudFunctionRequest('updateConversation', params).then((response) => {
    return response;
  });
};

// Used by experts to update combinedTags field in conversation.
export const updateSimplrCategories = async (simplrTag, conversationId, isAdmin, isOutOfScope) => {
  const expertId = getExpertId();

  let { tagHistory, combinedTags } = await getTagHistoryAndCombinedTagsForAConversation(conversationId);

  if (!tagHistory) {
    tagHistory = [];
  }

  // create an entry in the history
  const tagHistoryEntry = {
    simplrTag,
    isAdmin,
    expertId,
    time: new Date(),
    source: sourceTypes.SIMPLR,
    isOutOfScope,
  };

  tagHistory.push(tagHistoryEntry);

  const params = {
    conversationId,
    updatedFields: {
      combinedTags: {
        ...combinedTags,
        simplrCategories: [simplrTag],
      },
      tagHistory,
    },
  };

  return makeCloudFunctionRequest('updateConversation', params);
};

export const escalateConversation = (params) => {
  return makeCloudFunctionRequest('escalateConversation', params);
};

export const autoEscalateConversation = async (conversationId, platformDomain, platformTicketId) => {
  const expertId = getExpertId();
  const params = {
    conversationObjectId: conversationId,
    simplrExpertObjectId: expertId,
    platformDomain,
    platformTicketId,
    isManualEscalation: false,
  };
  await escalateConversation(params);
};

export { getAllTickets, getReviewTickets, getAssignedTicketsHelper, getConversationById, getEscalatedTickets };
